// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { Link } from '@/components/link';
import { TooltipTypography } from '@/components/tooltip-typography';
import { TransportRefLabel } from '@/components/transport-ref-label';
import {
  FO_STATUS_NOTIFICATION_LABELS,
  fulfillmentOrderStatusSchema,
} from '@/fulfillment/constants/fulfillment-order-status';
import {
  ContextAttribute,
  DrawnCell,
  KnownInboxEntry,
  fulfillmentOrderMessageTypeSchema,
  purchaseOrderMessageTypeSchema,
  returnOrderMessageTypeSchema,
} from '@/notification/api/notification-types';
import { reportUndrawableMessage } from '@/notification/utilities/report-undrawable-message';
import {
  PO_STATUS_NOTIFICATION_LABELS,
  poStatusSchema,
} from '@/purchase-order/constants/status';
import {
  RETURN_ORDER_REASON_LABELS,
  returnOrderReasonSchema,
} from '@/return-order/api/types';
import {
  RO_STATUS_NOTIFICATION_LABELS,
  roStatusSchema,
} from '@/return-order/constants/status';
import { SHIPMENT_STATUS_CONFIG } from '@/shipment/constants/shipment-status';
import {
  MEDIUM_DATE_FORMAT,
  toRecordedDate,
  toRecordedTimestamp,
} from '@/utilities/date-time';
import { pluralize } from '@/utilities/pluralize';
import { Box, Stack, Typography, TypographyProps } from '@mui/material';
import { ReactNode } from 'react';

interface Props {
  messageType: KnownInboxEntry['messageType'];
  columns: DrawnCell[][];
  details: DrawnCell[];
}

/** Renders a message's detail, laid out the way its template says. */
export function NotificationBody(props: Props) {
  return (
    <Stack spacing={1.5}>
      <Box
        display="grid"
        gridTemplateColumns={`repeat(${props.columns.length}, minmax(0, 1fr))`}
        gap={2}
      >
        {/* A column with no cells draws as the blank that holds its share */}
        {props.columns.map((column, index) => (
          <Stack key={index} spacing={0.5}>
            {column.map((cell, cellIndex) => (
              <BodyCell
                key={`${cell.label}-${cellIndex}`}
                messageType={props.messageType}
                cell={cell}
              />
            ))}
          </Stack>
        ))}
      </Box>
      {props.details.length > 0 && (
        <Stack spacing={0.5}>
          {props.details.map((cell, index) => (
            <BodyCell
              key={`${cell.label}-${index}`}
              messageType={props.messageType}
              cell={cell}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

interface BodyCellProps {
  messageType: KnownInboxEntry['messageType'];
  cell: DrawnCell;
}

// A cell the message carries nothing for is dropped before it reaches here, so
// every cell drawn has something to say.
function BodyCell(props: BodyCellProps) {
  const value = props.cell.value;
  if (props.cell.isText) {
    return (
      <Typography variant={toTypographyVariant(props.cell.variant)}>
        {props.cell.label}
      </Typography>
    );
  }
  if (value === null) {
    return null;
  }
  if (typeof value !== 'string' && value.kind === 'source') {
    return null;
  }
  if (typeof value !== 'string' && value.kind === 'items') {
    return <BodyItemList cell={props.cell} value={value} />;
  }
  if (typeof value !== 'string' && value.kind === 'shipments') {
    return <BodyShipmentList cell={props.cell} value={value} />;
  }
  return (
    <Stack direction="row" spacing={0.5} alignItems="baseline">
      <Typography variant="body2" color="text.secondary" component="span">
        {props.cell.label}:
      </Typography>
      <Typography variant="body2" component="span">
        <CellValue
          messageType={props.messageType}
          contextKey={props.cell.key}
          value={value}
          isDateOnly={props.cell.isDateOnly}
        />
      </Typography>
      {props.cell.note !== '' && (
        <Typography variant="body2" color="text.secondary" component="span">
          ({props.cell.note})
        </Typography>
      )}
    </Stack>
  );
}

// The template sets a text cell on the typography scale, and body text is what
// a cell that says nothing about it takes.
function toTypographyVariant(
  variant: DrawnCell['variant']
): TypographyProps['variant'] {
  return variant === undefined ? 'body2' : TEXT_VARIANTS[variant];
}

const TEXT_VARIANTS: Record<
  NonNullable<DrawnCell['variant']>,
  TypographyProps['variant']
> = {
  SUBTITLE2: 'subtitle2',
};

interface CellValueProps {
  messageType: KnownInboxEntry['messageType'];
  contextKey: string;
  value: ContextAttribute;
  isDateOnly: boolean;
}

// How a value draws follows from what it is, so nothing in the template says
// how to draw it. A record draws as a link because the screen knows where each
// kind of record routes.
function CellValue(props: CellValueProps) {
  const value = props.value;
  if (typeof value === 'string') {
    if (props.contextKey === 'returnType') {
      const reason = returnOrderReasonSchema.safeParse(value.toLowerCase());
      return (
        <>{reason.success ? RETURN_ORDER_REASON_LABELS[reason.data] : value}</>
      );
    }
    return <>{toStatusLabel(value, props.messageType)}</>;
  }
  switch (value.kind) {
    case 'project':
      return (
        <Link
          to="/projects/$projectKey"
          params={{ projectKey: value.project.key }}
          underline="hover"
        >
          {value.project.name}
        </Link>
      );
    case 'customer':
      return <>{value.customer.name}</>;
    case 'location':
      return (
        <Link
          to="/locations/$locationKey"
          params={{ locationKey: value.location.key }}
          underline="hover"
        >
          {value.location.name}
        </Link>
      );
    case 'shipment':
      return (
        <Link
          to="/shipments/$shipmentKey"
          params={{ shipmentKey: value.shipment.key }}
          underline="hover"
        >
          {value.shipment.shipmentNumber}
        </Link>
      );
    case 'fulfillmentOrder':
      return (
        <Link
          to="/fulfillments/$fulfillmentKey"
          params={{ fulfillmentKey: value.order.key }}
          underline="hover"
        >
          {value.order.number}
        </Link>
      );
    case 'purchaseOrder':
      return (
        <Link
          to="/purchase-orders/$purchaseOrderKey"
          params={{ purchaseOrderKey: value.order.key }}
          underline="hover"
        >
          {value.order.number}
        </Link>
      );
    case 'returnOrder':
      return (
        <Link
          to="/return-orders/$returnOrderKey"
          params={{ returnOrderKey: value.order.key }}
          underline="hover"
        >
          {value.order.number}
        </Link>
      );
    case 'instant':
      // Read in the zone the record kept, which is usually not the reader's, so
      // everybody sees the moment it named, and says which zone that was. Only
      // the milestone the message is about spends a line on the clock
      if (props.isDateOnly) {
        return (
          <TooltipTypography
            variant="body2"
            component="span"
            tooltip={toRecordedTimestamp(value.at, value.timezone)}
          >
            {toRecordedDate(value.at, value.timezone)}
          </TooltipTypography>
        );
      }
      return <>{toRecordedTimestamp(value.at, value.timezone)}</>;
    case 'day':
      // A calendar day carries no zone, so it reads as written
      return <>{value.on.toLocaleString(MEDIUM_DATE_FORMAT)}</>;
    case 'carrier':
      return (
        <TransportRefLabel
          carrier={value.carrier}
          carrierName={value.name ?? null}
          trackingNumber={value.trackingNumber}
        />
      );
    case 'items':
      // Drawn by BodyItemList, which BodyCell routes to before reaching here
      return null;
    case 'shipments':
      // Drawn by BodyShipmentList, which BodyCell routes to before reaching here
      return null;
    case 'source':
      return null;
    default:
      return toUndrawableKind(value);
  }
}

// A kind nothing draws is a contract the screen has not caught up with, so the
// compiler names it here rather than the reader finding an empty cell.
function toUndrawableKind(value: never): null {
  reportUndrawableMessage(
    'Inbox message carries an attribute kind nothing draws',
    'unknown',
    value
  );
  return null;
}

interface BodyListProps {
  label: string;
  children: ReactNode;
}

/** Renders a label above its bulleted entries. */
function BodyList(props: BodyListProps) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2" color="text.secondary">
        {props.label}:
      </Typography>
      <Stack component="ul" spacing={0.25} marginY={0} paddingLeft={3}>
        {props.children}
      </Stack>
    </Stack>
  );
}

interface BodyItemListProps {
  cell: DrawnCell;
  value: Extract<ContextAttribute, { kind: 'items' }>;
}

/** Renders a list attribute as bullets. */
function BodyItemList(props: BodyItemListProps) {
  const items = props.value.items.slice(0, props.cell.maxEntries);
  const remainingCount = props.value.items.length - items.length;
  return (
    <BodyList label={props.cell.label}>
      {items.map((item, index) => (
        <Typography
          key={`${item.name}-${index}`}
          component="li"
          variant="body2"
        >
          {item.quantity !== '' && `${item.quantity} × `}
          {item.name}
        </Typography>
      ))}
      {remainingCount > 0 && (
        <Typography component="li" variant="body2">
          and {pluralize(remainingCount, { singular: 'more item' })}
        </Typography>
      )}
    </BodyList>
  );
}

interface BodyShipmentListProps {
  cell: DrawnCell;
  value: Extract<ContextAttribute, { kind: 'shipments' }>;
}

/** Renders linked shipments as bullets with cancellation markers. */
function BodyShipmentList(props: BodyShipmentListProps) {
  const entries = props.value.entries.slice(0, props.cell.maxEntries);
  const remainingCount = props.value.entries.length - entries.length;
  return (
    <BodyList label={props.cell.label}>
      {entries.map((entry) => (
        <Typography key={entry.shipment.key} component="li" variant="body2">
          <Link
            to="/shipments/$shipmentKey"
            params={{ shipmentKey: entry.shipment.key }}
            underline="hover"
          >
            {entry.shipment.shipmentNumber}
          </Link>
          {entry.isCancelled && ' (cancelled)'}
        </Typography>
      ))}
      {remainingCount > 0 && (
        <Typography component="li" variant="body2">
          and{' '}
          {pluralize(remainingCount, {
            singular: 'more shipment',
          })}
        </Typography>
      )}
    </BodyList>
  );
}

// Keyed by the status as it is stored, so a status this bundle does not know
// reads as it was written rather than as nothing.
const SHIPMENT_STATUS_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(SHIPMENT_STATUS_CONFIG).flatMap(([status, config]) =>
    typeof config.label === 'string' ? [[status, config.label]] : []
  )
);

// A status reads here the way it reads everywhere else rather than shouting its
// stored name; anything else is text and passes straight through.
function toStatusLabel(
  text: string,
  messageType: KnownInboxEntry['messageType']
): string {
  return (
    toOrderStatusLabel(text, messageType) ??
    SHIPMENT_STATUS_LABELS[text.toLowerCase()] ??
    text
  );
}

// Every order kind shares the same status values, so the message's order kind
// decides whose label a status reads with.
function toOrderStatusLabel(
  text: string,
  messageType: KnownInboxEntry['messageType']
): string | null {
  if (fulfillmentOrderMessageTypeSchema.safeParse(messageType).success) {
    const status = fulfillmentOrderStatusSchema.safeParse(text);
    return status.success ? FO_STATUS_NOTIFICATION_LABELS[status.data] : null;
  }

  if (purchaseOrderMessageTypeSchema.safeParse(messageType).success) {
    const status = poStatusSchema.safeParse(text);
    return status.success ? PO_STATUS_NOTIFICATION_LABELS[status.data] : null;
  }

  if (returnOrderMessageTypeSchema.safeParse(messageType).success) {
    const status = roStatusSchema.safeParse(text);
    return status.success ? RO_STATUS_NOTIFICATION_LABELS[status.data] : null;
  }

  return null;
}
